// Drawing a portion of the curve. `DrawRange` is a pair of fractions of the
// segment index space (0 = first vertex, 1 = last), shared by both renderers,
// the trace slider and the play animation. `segmentWindow` maps it onto a
// buffer that may be decimated by a stride.

export interface DrawRange { start: number; end: number }

export const FULL_RANGE: DrawRange = { start: 0, end: 1 };

export interface SegmentWindow {
  first: number;   // first buffer segment to draw
  count: number;   // number of buffer segments to draw (0 = nothing)
  lo: number;      // fraction of the first drawn segment to skip (0..1)
  hi: number;      // fraction of the last drawn segment to keep (0..1)
}

const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);

/**
 * Buffer segment j spans original indices [j*stride, min((j+1)*stride, total)]:
 * a decimated buffer always keeps the last vertex, so its final segment may
 * be shorter than `stride`. `total` is the segment count at stride 1 and
 * `bufSegs` the segment count in the buffer.
 */
export function segmentWindow(r: DrawRange, total: number, stride: number, bufSegs: number): SegmentWindow {
  const s = Math.max(1, stride | 0);
  const a = clamp01(r.start) * total;
  const b = clamp01(r.end) * total;
  if (!(b > a) || total <= 0 || bufSegs <= 0) return { first: 0, count: 0, lo: 0, hi: 1 };
  const first = Math.min(bufSegs - 1, Math.floor(a / s));
  const last = Math.min(bufSegs, Math.max(first + 1, Math.ceil(b / s)));   // exclusive
  const span = (j: number) => (j === bufSegs - 1 ? Math.max(1e-9, total - j * s) : s);
  const lo = clamp01((a - first * s) / span(first));
  const hi = clamp01((b - (last - 1) * s) / span(last - 1));
  return { first, count: last - first, lo, hi };
}

export function isFullRange(r: DrawRange): boolean {
  return r.start <= 0 && r.end >= 1;
}
